import {DeliverySection as DeliverySectionType} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/basket_sections.types';
import {useFormatDateWithShortWeekName} from '@aboutyou/i18n';
import {BasketSection, DeliverySection} from '@aboutyou/ui-components';
import {isCaseTypeCheck} from '@aboutyou/utils';
import React, {useState} from 'react';
import styled from 'styled-components';
import {useActivatePreselectedBestCouponABTest} from '../../../features/Coupons/useActivatePreselectedBestCouponABTest';
import {useProductImageType} from '../../../shared-deprecated/useProductImageType';
import {MerchantLegalInfo} from '../../MerchantLegalInfo/MerchantLegalInfo';
import {useShouldCheckForPriceRise} from '../hooks/useShouldCheckForPriceRise';
import {BasketProductCard} from './Components/BasketProductCard';
import {DeliveriesProps} from './Deliveries';
import {useShowDeliveriesAsDayDates} from './hooks/useShowDeliveriesAsDayDates';

export const DeliveriesView: React.FC<
  DeliverySectionType & DeliveriesProps
> = ({type, onBasketQuantityChange, onBasketItemRemovedSuccessfully}) => {
  const formatDateWithShortWeekName = useFormatDateWithShortWeekName();
  const deliveries = isCaseTypeCheck('delivery', type)
    ? type.delivery.deliveries
    : [];

  const deliveriesWithDay = useShowDeliveriesAsDayDates(deliveries);
  const shouldCheckForPriceRise = useShouldCheckForPriceRise(deliveries);

  const [productImageType] = useProductImageType();
  const [legalInfoDetails, setLegalInfoDetails] = useState<
    | {
        size: number;
        productId: number;
      }
    | undefined
  >(undefined);

  const handleLegalInfoClick = (options: {size: number; productId: number}) => {
    setLegalInfoDetails(options);
  };

  useActivatePreselectedBestCouponABTest();

  if (!deliveriesWithDay.length) {
    return null;
  }

  return (
    <BasketSection>
      {deliveriesWithDay.map((data, index) => {
        const deliveryDate = `${formatDateWithShortWeekName(
          data.deliveryPeriod.startDate,
        )} - ${formatDateWithShortWeekName(data.deliveryPeriod.endDate)}`;

        return (
          <StyledDeliverySection
            headline={data.headline}
            shippedBy={data.shippingMerchant?.shippedBy.text}
            deliveryDate={data.showDateAsDayTitle || deliveryDate}
            hideInfoText={index !== deliveriesWithDay.length - 1}
            key={index}
            withoutBottomGap={index + 1 !== deliveriesWithDay.length}
            withoutRoundedTopCorners={index > 0}
            withoutRoundedBottomCorners={index + 1 < deliveriesWithDay.length}
            hightlightDateLabel={data.showDatesAsDays}
          >
            {data.items.map(item => {
              return (
                <BasketProductCard
                  key={`${item.product.id}-${item.size.id}`}
                  item={item}
                  preferredProductImageType={productImageType}
                  onBasketQuantityChange={onBasketQuantityChange}
                  onBasketItemRemovedSuccessfully={
                    onBasketItemRemovedSuccessfully
                  }
                  onLegalInfoClick={handleLegalInfoClick}
                  shouldCheckForPriceRise={shouldCheckForPriceRise}
                />
              );
            })}
          </StyledDeliverySection>
        );
      })}
      {legalInfoDetails && (
        <MerchantLegalInfo
          sizeId={legalInfoDetails.size}
          productId={legalInfoDetails.productId}
          onClose={() => {
            setLegalInfoDetails(undefined);
          }}
        />
      )}
    </BasketSection>
  );
};

const StyledDeliverySection = styled(DeliverySection)`
  &:last-child {
    padding-bottom: 0;
  }
`;
